"use client"

import * as React from "react"

import { createPersistedStore, usePersistedStore } from "@/lib/persisted-store"

import { currentActor } from "@/lib/objects/track"
import {
  removeLink,
  upsertLink,
  type CrosswalkLink,
  type ExamCategory,
  suggestCrosswalk,
} from "@/lib/exam-categories"

export const CROSSWALK_STORE_KEY = "shell:exam-category-crosswalk:v1"

type Store = { links: CrosswalkLink[]; seeded: boolean }

const crosswalkStore = createPersistedStore<Store>(CROSSWALK_STORE_KEY, () => ({
  links: [],
  seeded: false,
}))

/**
 * The director's mapping from course content categories to program standards.
 * One store, read by Settings (which edits it), Reports (which counts through
 * it) and the question views (which show what a question counts toward).
 */
export function useCategoryCrosswalk() {
  const [store, setStore] = usePersistedStore(crosswalkStore)

  /** Leo's first pass, once. Later it only adds what the director asks for. */
  const seedSuggestions = React.useCallback(
    (input: {
      content: ExamCategory[]
      standards: ExamCategory[]
      all: ExamCategory[]
    }) => {
      setStore((current) => {
        if (current.seeded) return current
        return {
          seeded: true,
          links: suggestCrosswalk(input).reduce(upsertLink, current.links),
        }
      })
    },
    [setStore],
  )

  const confirm = React.useCallback(
    (contentId: string, standardId: string) =>
      setStore((current) => ({
        ...current,
        links: upsertLink(current.links, {
          contentId,
          standardId,
          status: "confirmed",
          by: currentActor(),
          at: new Date().toISOString(),
        }),
      })),
    [setStore],
  )

  /** Confirm every pending suggestion, for the given standard categories. */
  const confirmSuggested = React.useCallback(
    (standardIds: ReadonlySet<string>) =>
      setStore((current) => ({
        ...current,
        links: current.links.map((link) =>
          link.status === "suggested" && standardIds.has(link.standardId)
            ? { ...link, status: "confirmed" as const, by: currentActor(), at: new Date().toISOString() }
            : link,
        ),
      })),
    [setStore],
  )

  const remove = React.useCallback(
    (contentId: string, standardId: string) =>
      setStore((current) => ({
        ...current,
        links: removeLink(current.links, { contentId, standardId }),
      })),
    [setStore],
  )

  return {
    links: store.links,
    seeded: store.seeded,
    seedSuggestions,
    confirm,
    confirmSuggested,
    remove,
  }
}
